import React from 'react';
import {styled} from '@mui/material/styles';
import {Typography, Button, TableContainer, Table, TableHead, TableCell, TableBody, TableRow} from '@mui/material';
import {CheckCircle, ArrowForward} from '@mui/icons-material';

const SamplesReviewBodyStyled = styled('div')({
    padding: '4%'
});

const CheckCircleStyled = styled(CheckCircle)({
    width: 40,
    height: 40
});

const ArrowForwardStyled = styled(ArrowForward)({
    width: 60,
    height: 60
});

const ContainerStyled = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
});

const TableContainerStyled = styled(TableContainer)({
    maxWidth: '330px',
    border: '1px solid #CAC4CF',
    borderColor: 'outline-variant',
    borderRadius: '5px',
    margin: '3% 2%'
});

interface SampleVariableValueTableProps {
    changedSamples: any;
    /** The key, for example 'Sex' */
    variable: string;
    /** The value, for example 'Male' */
    value: string;
}

/**
 * Component that displays a table with sample names and their corresponding
 * variable values. As this is used for renaming of a certain variable's
 * certain value, every row in the table shows the same value.
 */
const SampleVariableValueTable = ({changedSamples, variable, value}: SampleVariableValueTableProps) => {
    return (
        <TableContainerStyled>
            <Table>
                <TableHead sx={{bgcolor: 'surface-container'}}>
                    <TableRow>
                        <TableCell width={'50%'} sx={{padding: '7px 13px'}}>
                            SAMPLE NAME
                        </TableCell>
                        <TableCell width={'35%'} sx={{padding: '7px 13px'}}>
                            {variable}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {changedSamples.map((sample: any) => (
                        <TableRow key={sample.id}>
                            <TableCell width={'50%'} sx={{padding: '7px 13px'}}>
                                {sample.name}
                            </TableCell>
                            <TableCell width={'35%'} sx={{padding: '7px 13px'}}>
                                {value}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainerStyled>
    );
};

interface SamplesToReview {
    changedSamples: any[];
    key: string;
    oldValue: string;
    newValue: string;
}

interface SamplesReviewProps {
    samplesToReview: SamplesToReview;
    onClose?: () => void;
}

/**
 * Shows side by side tables of the samples whose values have been changed.
 * User cannot make any changes here.
 */
export const SamplesReview: React.FC<SamplesReviewProps> = ({samplesToReview, onClose}) => {
    return (
        <SamplesReviewBodyStyled>
            <Typography variant="headline" data-testid="sample-update-header" size="medium" mb={1}>
                <CheckCircleStyled />
                Updated {samplesToReview?.changedSamples?.length} Sample{samplesToReview?.changedSamples?.length != 1 ? 's' : ''}
            </Typography>
            <Typography variant="body" size="large">
                Value labels for the following samples have been changed:
            </Typography>
            <ContainerStyled>
                <SampleVariableValueTable
                    changedSamples={samplesToReview.changedSamples}
                    variable={samplesToReview.key}
                    value={samplesToReview.oldValue}
                />

                <ArrowForwardStyled />

                <SampleVariableValueTable
                    changedSamples={samplesToReview.changedSamples}
                    variable={samplesToReview.key}
                    value={samplesToReview.newValue}
                />
            </ContainerStyled>

            <Button onClick={onClose} variant="contained" fullWidth>
                Close
            </Button>
        </SamplesReviewBodyStyled>
    );
};
